/**
 * VENUE DETAIL — one place, everything about it.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT CHANGED AND WHY
 *
 * The old page was five tabs: About · What's On · Events · Sports ·
 * Facilities. Four of the five backing tables held **0 rows**, so four tabs
 * out of five were guaranteed empty on every venue in production — the user
 * paid a tab bar and a click to be told "nothing listed yet", four times.
 *
 * It is now one scroll, ordered the way the brief asks and the way somebody
 * actually reads a pub listing:
 *
 *     photos → the essentials → FEATURE ICONS → what's on here → details
 *
 * Empty sections simply do not render, so a bare venue is short rather than
 * being five empty rooms.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * EVENTS ARE BY REFERENCE, NEVER COPIED
 *
 * "What's on here" is assembled from THREE separate queries against the
 * venue's id — `/api/events?venueId=`, `/api/venues/:id/activities` and
 * `/api/venues/:id/sports`. Not one field of any of them is stored on the
 * venue row, which is the entire discipline of this rewrite: a venue can have
 * no events, an event can have no venue, and neither has to lie about the
 * other. Denormalising "next event" onto `bar_listings` would be the exact
 * mistake `events.venue` / `events.address` / `events.lat` already made
 * (EVENTS_AUDIT.md §2.1).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * FEATURE ICONS
 *
 * `facility_definitions.icon` stores a kebab-case **lucide icon name**
 * ("circle-dot", "target", "trees"). The old page rendered that column as a
 * character — `{icon.length <= 2 ? icon : "✓"}` — so every real definition
 * fell through to a generic tick. hub/venue-utils `facilityIcon()` maps the
 * name to the component it always meant.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "wouter";
import {
  Calendar, ChevronLeft, Clock, Facebook, Globe, Heart, Instagram, Mail,
  MapPin, Phone, Repeat, Share2, Trophy,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSEO } from "@/hooks/use-seo";
import { useToast } from "@/hooks/use-toast";
import { QueryState, isNotFoundError } from "@/components/QueryState";
import { cn } from "@/lib/utils";
import {
  Chip, ELEVATION, FOCUS_RING, FacilityGrid, RADII, TYPE, WhatsOnRow,
  DAY_NAMES_FULL, formatTime, amenitiesToFacilitySlugs, recurrenceLabel,
  resolveFacilities, shortDate, venueTypeLabel, venueTypeTone,
  useEvents, useFacilityDefinitions, useVenue, useVenueActivityList,
  useVenueFacilities, useVenueSports,
  type HubVenue, type WhatsOnEntry,
} from "@/components/hub";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const DAYS_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
const DAY_LABELS: Record<string, string> = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
  thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};

function Section({
  title, subtitle, children, action,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className={cn(TYPE.title3, "text-slate-900")}>{title}</h2>
          {subtitle && <p className={cn(TYPE.footnote, "text-slate-500 mt-0.5")}>{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn(RADII.md, ELEVATION.sm, "bg-white border border-slate-200/80 p-4 sm:p-5", className)}>
      {children}
    </div>
  );
}

/* ── Opening hours ────────────────────────────────────────────────────── */

function OpeningHours({ hours }: { hours: NonNullable<HubVenue["openingHours"]> }) {
  // JS getDay() is Sunday-first; DAYS_ORDER is Monday-first.
  const todayKey = DAYS_ORDER[(new Date().getDay() + 6) % 7];

  return (
    <dl className="divide-y divide-slate-100">
      {DAYS_ORDER.map((day) => {
        const info = hours[day];
        const closed = !info || info.closed;
        const isToday = day === todayKey;
        return (
          <div key={day} className={cn("flex items-center justify-between py-2", isToday && "font-semibold")}>
            <dt className={cn(TYPE.callout, isToday ? "text-[#1e3a5f]" : "text-slate-600")}>
              {DAY_LABELS[day]}
              {isToday && <span className={cn(TYPE.overline, "ml-2 text-slate-400")}>Today</span>}
            </dt>
            <dd className={cn(TYPE.callout, closed ? "text-slate-400" : isToday ? "text-[#1e3a5f]" : "text-slate-700")}>
              {closed ? "Closed" : `${formatTime(info!.open)} – ${formatTime(info!.close)}`}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────── */

export default function VenueDetail() {
  const { slug, id } = useParams<{ slug?: string; id?: string }>();
  const identifier = slug ?? id ?? "";
  const { user } = useAuth();
  const { toast } = useToast();

  const [followed, setFollowed] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const venueQuery = useVenue(identifier);
  const venue = venueQuery.data;
  const venueId = venue?.id;

  const facilitiesQuery = useVenueFacilities(venueId);
  const { data: defsData } = useFacilityDefinitions();
  const activitiesQuery = useVenueActivityList(venueId);
  const eventsQuery = useEvents({ venueId, limit: 50, enabled: !!venueId });
  const sportsQuery = useVenueSports(venueId);

  useSEO({
    title: venue ? `${venue.name} — ${venueTypeLabel(venue.venueType)} in ${venue.town ?? "Ayrshire"}` : undefined,
    description: venue
      ? venue.description ?? `${venue.name}, a ${venueTypeLabel(venue.venueType).toLowerCase()} in ${venue.town ?? "Ayrshire"}. See what's on, facilities, opening hours and how to get there.`
      : undefined,
    image: venue?.coverPhoto ?? undefined,
  });

  useEffect(() => { setLightbox(null); }, [identifier]);

  /* Facility slugs: structured rows UNION legacy amenity array, mirroring the
     server's own matching rule in api-server/src/lib/facilityFilter.ts. If the
     two ever disagree the icons here can never contradict the Places filter
     that surfaced this venue. */
  const facilities = useMemo(() => {
    if (!venue) return [];
    const slugs = new Set(amenitiesToFacilitySlugs(venue.amenities));
    for (const row of facilitiesQuery.data?.items ?? []) {
      if (row.isActive !== false) slugs.add(row.definition.slug);
    }
    return resolveFacilities(slugs, defsData?.items ?? []);
  }, [venue, facilitiesQuery.data, defsData]);

  /** Upcoming, by reference. Events + recurring activities, one list, real dates. */
  const whatsOnEntries = useMemo<WhatsOnEntry[]>(() => {
    if (!venue) return [];

    const events: WhatsOnEntry[] = (eventsQuery.data?.items ?? []).map((e) => {
      const price = (e.price ?? "").trim();
      const isFree = price === "" || price === "0" || price.toLowerCase() === "free";
      return {
        key: `e${e.id}`,
        kind: "event",
        href: `/event/${e.id}`,
        title: e.name,
        summary: e.shortSummary ?? e.description ?? null,
        category: e.category || null,
        date: (e.eventDate ?? "").slice(0, 10),
        time: e.eventTime ?? null,
        imageUrl: e.imageUrl ?? null,
        town: e.town ?? null,
        venueName: venue.name,
        venueHref: null,
        priceText: isFree ? null : price,
        isFree,
        featured: !!e.featured,
        recurrence: null,
      };
    });

    /* Recurring activities have no single date — they are a PATTERN. Rather
       than invent one, they are listed separately below with their rule. */
    return events.sort((a, b) => a.date.localeCompare(b.date));
  }, [venue, eventsQuery.data]);

  const activities = activitiesQuery.data?.items ?? [];
  const sports = sportsQuery.data?.items ?? [];

  async function handleFollow() {
    if (!user || !venue) return;
    const next = !followed;
    setFollowLoading(true);
    setFollowed(next); // optimistic
    try {
      const res = await fetch(`/api/venues/${venue.id}/follow`, {
        method: next ? "POST" : "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(String(res.status));
    } catch {
      setFollowed(!next); // roll back — the server never recorded it
      toast({
        title: next ? "Couldn't follow this venue" : "Couldn't unfollow this venue",
        description: "Check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setFollowLoading(false);
    }
  }

  function handleShare() {
    if (!venue) return;
    if (navigator.share) {
      void navigator.share({ title: venue.name, url: window.location.href });
    } else {
      void navigator.clipboard.writeText(window.location.href);
      toast({ title: "Link copied" });
    }
  }

  const isMissing = !venueQuery.isLoading && (isNotFoundError(venueQuery.error) || (!venueQuery.isError && !venue));

  if (venueQuery.isLoading || (venueQuery.isError && !isMissing)) {
    return (
      <QueryState
        isLoading={venueQuery.isLoading}
        isError={venueQuery.isError}
        onRetry={venueQuery.refetch}
        skeleton={
          <div className="container mx-auto px-4 py-6 max-w-4xl">
            <div className={cn(RADII.md, "h-56 sm:h-72 skeleton-shimmer bg-slate-200/70 mb-4")} />
            <div className="h-7 w-2/3 rounded skeleton-shimmer bg-slate-200/70 mb-3" />
            <div className="h-4 w-1/3 rounded skeleton-shimmer bg-slate-200/70" />
          </div>
        }
        errorTitle="Couldn't load this venue"
        errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
        errorAction={{ label: "Back to Places", href: "/places" }}
        fullPage
      />
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-4">
        <div className="text-center">
          <p className="text-5xl mb-4" aria-hidden>🍺</p>
          <p className={cn(TYPE.title2, "text-slate-900 mb-1")}>Venue not found</p>
          <p className={cn(TYPE.callout, "text-slate-500 mb-6")}>This venue may have been removed.</p>
          <Link
            href="/places"
            className={cn(RADII.sm, TYPE.callout, FOCUS_RING, "inline-block px-5 py-2.5 bg-[#1e3a5f] text-white font-semibold")}
          >
            Back to Places
          </Link>
        </div>
      </div>
    );
  }

  const photos = (venue.photos ?? []).filter(Boolean);
  const address = [venue.address, venue.town, venue.postcode].filter((s) => s && s.trim()).join(", ");
  const mapsHref = `https://www.google.com/maps/search/${encodeURIComponent([venue.name, address].filter(Boolean).join(", "))}`;
  const hasContact = !!(venue.phone || venue.website || venue.email || venue.facebook || venue.instagram);
  const hasExtraInfo = !!(venue.ageRestriction || venue.accessibilityInfo || venue.foodInfo);

  const actionClass = cn(
    TYPE.caption, RADII.pill, FOCUS_RING,
    "inline-flex items-center gap-1.5 h-9 px-3.5 border transition-colors cursor-pointer",
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-4 sm:py-6 max-w-4xl">
        <Link
          href="/places"
          className={cn(TYPE.footnote, FOCUS_RING, RADII.xs, "inline-flex items-center gap-1 text-slate-500 hover:text-[#1e3a5f] transition-colors mb-3")}
        >
          <ChevronLeft size={15} aria-hidden /> Places
        </Link>

        {/* ── Hero ────────────────────────────────────────────────────── */}
        <div className={cn(RADII.md, ELEVATION.sm, "bg-white border border-slate-200/80 overflow-hidden")}>
          <div className="relative aspect-[21/9] sm:aspect-[3/1] bg-slate-100">
            {venue.coverPhoto ? (
              <img src={venue.coverPhoto} alt="" className="w-full h-full object-cover" />
            ) : (
              /* No venue has a cover photo yet — all three are empty strings
                 (MIGRATION_REPORT §1.4). A branded gradient reads as "no photo
                 yet" rather than as a broken image. */
              <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#0c1e33] to-[#1e3a5f]">
                <span className="text-4xl opacity-40" aria-hidden>🍺</span>
              </div>
            )}
          </div>

          <div className="p-4 sm:p-5">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Chip tone={venueTypeTone(venue.venueType)} size="sm">{venueTypeLabel(venue.venueType)}</Chip>
              {venue.town && <Chip size="sm">{venue.town}</Chip>}
              {venue.isFeatured && <Chip tone="gold" size="sm">⭐ Featured</Chip>}
            </div>

            <h1 className={cn(TYPE.title1, "text-slate-900")}>{venue.name}</h1>

            {address && (
              <p className={cn(TYPE.subhead, "text-slate-500 mt-1 inline-flex items-start gap-1.5")}>
                <MapPin size={13} className="mt-0.5 shrink-0" aria-hidden />
                {address}
              </p>
            )}

            {/* Actions. Call and Directions first — on a phone, standing
                outside, those are the two anyone actually presses. */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {venue.phone && (
                <a href={`tel:${venue.phone}`} className={cn(actionClass, "bg-[#1e3a5f] text-white border-[#1e3a5f] hover:bg-[#1e3a5f]/90")}>
                  <Phone size={13} aria-hidden /> Call
                </a>
              )}
              {(venue.address || venue.town) && (
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noreferrer"
                  className={cn(actionClass, "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900")}
                >
                  <MapPin size={13} aria-hidden /> Directions
                </a>
              )}
              {user && (
                <button
                  type="button"
                  onClick={() => void handleFollow()}
                  disabled={followLoading}
                  aria-pressed={followed}
                  className={cn(
                    actionClass, "disabled:opacity-50",
                    followed
                      ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900",
                  )}
                >
                  <Heart size={13} className={followed ? "fill-red-500 text-red-500" : ""} aria-hidden />
                  {followed ? "Following" : "Follow"}
                </button>
              )}
              <button
                type="button"
                onClick={handleShare}
                className={cn(actionClass, "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900")}
              >
                <Share2 size={13} aria-hidden /> Share
              </button>
              {venue.businessId && (
                <Link href={`/businesses/${venue.businessId}`} className={cn(TYPE.caption, FOCUS_RING, "text-[#1e3a5f] hover:underline ml-auto")}>
                  Business listing →
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-7">
          {/* ── Photos ──────────────────────────────────────────────────
              Only rendered when there are extras beyond the cover. */}
          {photos.length > 0 && (
            <Section title="Photos">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {photos.map((src, i) => (
                  <button
                    key={`${src}-${i}`}
                    type="button"
                    onClick={() => setLightbox(src)}
                    className={cn(RADII.sm, FOCUS_RING, "aspect-square overflow-hidden bg-slate-100 cursor-zoom-in")}
                  >
                    <img src={src} alt={`${venue.name} photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                  </button>
                ))}
              </div>
            </Section>
          )}

          {venue.description && (
            <Section title="About">
              <Card>
                <p className={cn(TYPE.body, "text-slate-600 whitespace-pre-wrap")}>{venue.description}</p>
              </Card>
            </Section>
          )}

          {/* ── FEATURE ICONS ────────────────────────────────────────────
              High up, because on Places this is what the user filtered by and
              this page has to confirm it. Also the only structured, queryable
              thing we hold about a venue. */}
          {facilities.length > 0 && (
            <Section
              title="What's here"
              subtitle="Facilities at this venue"
              action={
                <Link href="/places" className={cn(TYPE.caption, FOCUS_RING, "text-[#1e3a5f] hover:underline shrink-0")}>
                  Find more like this →
                </Link>
              }
            >
              <FacilityGrid facilities={facilities} definitions={defsData?.items ?? []} />
            </Section>
          )}

          {venue.happyHourText && (
            <Section title="Happy hour">
              <div className={cn(RADII.md, "flex items-start gap-3 border border-amber-200 bg-amber-50 p-4")}>
                <span className="text-lg shrink-0" aria-hidden>🍻</span>
                <p className={cn(TYPE.body, "text-amber-900")}>{venue.happyHourText}</p>
              </div>
            </Section>
          )}

          {/* ── WHAT'S ON HERE ──────────────────────────────────────────
              BY REFERENCE. Three queries keyed on this venue's id; nothing
              about any of them is stored on the venue row. */}
          <Section
            title="What's on here"
            subtitle={
              whatsOnEntries.length || activities.length
                ? "Upcoming events and regular nights"
                : undefined
            }
            action={
              <Link href="/events" className={cn(TYPE.caption, FOCUS_RING, "text-[#1e3a5f] hover:underline shrink-0")}>
                All of What's On →
              </Link>
            }
          >
            {activitiesQuery.isLoading || eventsQuery.isLoading ? (
              <div className="space-y-2">
                {[0, 1].map((i) => (
                  <div key={i} className={cn(RADII.md, "h-16 skeleton-shimmer bg-slate-200/70")} />
                ))}
              </div>
            ) : whatsOnEntries.length === 0 && activities.length === 0 ? (
              <Card>
                <div className="flex items-start gap-3">
                  <Calendar size={18} className="text-slate-300 mt-0.5 shrink-0" aria-hidden />
                  <div>
                    <p className={cn(TYPE.bodyStrong, "text-slate-700")}>Nothing listed here yet</p>
                    <p className={cn(TYPE.footnote, "text-slate-500 mt-0.5")}>
                      {facilities.length > 0
                        ? "The facilities above are always here though — worth a call before you head down."
                        : "This venue hasn't added its events or regular nights yet."}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-3">
                      <Link href="/events" className={cn(TYPE.caption, FOCUS_RING, "text-[#1e3a5f] hover:underline")}>
                        See what's on elsewhere →
                      </Link>
                      <Link href="/submit-event" className={cn(TYPE.caption, FOCUS_RING, "text-slate-500 hover:underline")}>
                        Submit an event
                      </Link>
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                {whatsOnEntries.length > 0 && (
                  <div className="space-y-2">
                    {whatsOnEntries.map((entry) => <WhatsOnRow key={entry.key} entry={entry} />)}
                  </div>
                )}

                {/* Recurring nights are a PATTERN, not a date. Listed with
                    their rule rather than flattened into fake occurrences. */}
                {activities.length > 0 && (
                  <div>
                    <p className={cn(TYPE.overline, "text-slate-400 mb-2")}>Every week</p>
                    <div className="space-y-2">
                      {activities.map((a) => (
                        <div
                          key={a.id}
                          className={cn(RADII.md, "flex items-center gap-3 bg-white border border-slate-200/80 p-3")}
                        >
                          <div className={cn(RADII.sm, "w-11 h-11 shrink-0 grid place-items-center bg-[#e8eef5] text-[#1e3a5f]")}>
                            <Repeat size={17} aria-hidden />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={cn(TYPE.headline, "text-slate-900 line-clamp-1")}>{a.title}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className={cn(TYPE.footnote, "text-slate-500")}>
                                {recurrenceLabel(a.frequency, a.dayOfWeek) ??
                                  (a.dayOfWeek != null ? DAY_NAMES_FULL[a.dayOfWeek] : "")}
                              </span>
                              {a.startTime && (
                                <span className={cn(TYPE.footnote, "inline-flex items-center gap-1 text-slate-500")}>
                                  <Clock size={11} aria-hidden />
                                  {formatTime(a.startTime)}
                                  {a.endTime ? ` – ${formatTime(a.endTime)}` : ""}
                                </span>
                              )}
                              {a.bookingRequired && <Chip tone="warning" size="xs">Booking required</Chip>}
                            </div>
                          </div>
                          {a.isFree
                            ? <Chip tone="success" size="xs">Free</Chip>
                            : a.priceText
                              ? <span className={cn(TYPE.caption, "text-slate-600 shrink-0")}>{a.priceText}</span>
                              : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* ── Sport ─────────────────────────────────────────────────── */}
          {sports.length > 0 && (
            <Section title="Sport on the box">
              <div className="space-y-2">
                {sports.map((s) => (
                  <div key={s.id} className={cn(RADII.md, "flex items-center gap-3 bg-white border border-slate-200/80 p-3")}>
                    <div className={cn(RADII.sm, "w-11 h-11 shrink-0 grid place-items-center bg-emerald-50 text-emerald-700")}>
                      <Trophy size={17} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={cn(TYPE.headline, "text-slate-900 capitalize line-clamp-1")}>
                        {s.sport.replace(/_/g, " ")}
                        {s.fixtureName ? ` — ${s.fixtureName}` : ""}
                      </p>
                      <p className={cn(TYPE.footnote, "text-slate-500 mt-0.5")}>
                        {shortDate(s.startDate)}
                        {s.startTime ? ` · ${formatTime(s.startTime)}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Details ─────────────────────────────────────────────────
              Hours, contact and small print last: useful, but nobody arrives
              on a pub page to read the accessibility statement first. */}
          {(venue.openingHours || hasContact || hasExtraInfo) && (
            <Section title="Details">
              <div className="grid gap-3 md:grid-cols-2">
                {venue.openingHours && (
                  <Card>
                    <p className={cn(TYPE.overline, "text-slate-400 mb-2")}>Opening hours</p>
                    <OpeningHours hours={venue.openingHours} />
                  </Card>
                )}

                {hasContact && (
                  <Card>
                    <p className={cn(TYPE.overline, "text-slate-400 mb-2")}>Get in touch</p>
                    <ul className="space-y-2.5">
                      {venue.phone && (
                        <li className="flex items-center gap-2.5">
                          <Phone size={15} className="text-slate-400 shrink-0" aria-hidden />
                          <a href={`tel:${venue.phone}`} className={cn(TYPE.callout, FOCUS_RING, "text-[#1e3a5f] hover:underline")}>{venue.phone}</a>
                        </li>
                      )}
                      {venue.email && (
                        <li className="flex items-center gap-2.5 min-w-0">
                          <Mail size={15} className="text-slate-400 shrink-0" aria-hidden />
                          <a href={`mailto:${venue.email}`} className={cn(TYPE.callout, FOCUS_RING, "text-[#1e3a5f] hover:underline truncate")}>{venue.email}</a>
                        </li>
                      )}
                      {venue.website && (
                        <li className="flex items-center gap-2.5 min-w-0">
                          <Globe size={15} className="text-slate-400 shrink-0" aria-hidden />
                          <a
                            href={venue.website.startsWith("http") ? venue.website : `https://${venue.website}`}
                            target="_blank"
                            rel="noreferrer"
                            className={cn(TYPE.callout, FOCUS_RING, "text-[#1e3a5f] hover:underline truncate")}
                          >
                            {venue.website.replace(/^https?:\/\//, "")}
                          </a>
                        </li>
                      )}
                      {venue.facebook && (
                        <li className="flex items-center gap-2.5">
                          <Facebook size={15} className="text-slate-400 shrink-0" aria-hidden />
                          <a href={venue.facebook} target="_blank" rel="noreferrer" className={cn(TYPE.callout, FOCUS_RING, "text-[#1e3a5f] hover:underline")}>Facebook</a>
                        </li>
                      )}
                      {venue.instagram && (
                        <li className="flex items-center gap-2.5">
                          <Instagram size={15} className="text-slate-400 shrink-0" aria-hidden />
                          <a
                            href={venue.instagram.startsWith("http") ? venue.instagram : `https://instagram.com/${venue.instagram.replace(/^@/, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            className={cn(TYPE.callout, FOCUS_RING, "text-[#1e3a5f] hover:underline")}
                          >
                            Instagram
                          </a>
                        </li>
                      )}
                    </ul>
                  </Card>
                )}

                {hasExtraInfo && (
                  <Card className="md:col-span-2">
                    <p className={cn(TYPE.overline, "text-slate-400 mb-2")}>Good to know</p>
                    <ul className={cn(TYPE.callout, "space-y-1.5 text-slate-600")}>
                      {venue.ageRestriction && <li>🔞 <span className="font-semibold">Age:</span> {venue.ageRestriction}</li>}
                      {venue.accessibilityInfo && <li>♿ <span className="font-semibold">Accessibility:</span> {venue.accessibilityInfo}</li>}
                      {venue.foodInfo && <li>🍽️ <span className="font-semibold">Food:</span> {venue.foodInfo}</li>}
                    </ul>
                  </Card>
                )}
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* ── Photo lightbox ────────────────────────────────────────────── */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Venue photo"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="" className="max-h-full max-w-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}
